/**
 * What each support handoff card in a chat has become (opened or dismissed),
 * read off the messages' `metadata.support_cards` and saved back on the
 * message through the chat. Outside a live chat the card keeps its state
 * locally.
 */
import { createContext, useContext, useMemo } from "react";

import type {
  AgentMessageMetadata,
  SupportCardState
} from "@/features/agents/useAgentChat";

export type SupportCards = {
  stateOf: (toolCallId: string) => SupportCardState | null;
  setState: (toolCallId: string, state: SupportCardState) => void;
};

const SupportCardsContext = createContext<SupportCards | null>(null);

export const SupportCardsProvider = SupportCardsContext.Provider;

export const useSupportCardsContext = (): SupportCards | null =>
  useContext(SupportCardsContext);

type SupportChat = {
  messages: readonly { metadata?: unknown }[];
  setSupportCard: (toolCallId: string, state: SupportCardState) => void;
};

/** `metadata.support_cards` across the messages, by tool call id. */
export const supportCardStatesIn = (
  messages: SupportChat["messages"]
): Map<string, SupportCardState> => {
  const out = new Map<string, SupportCardState>();
  for (const m of messages) {
    const cards =
      (m.metadata as AgentMessageMetadata | undefined)?.support_cards ?? {};
    for (const [id, card] of Object.entries(cards)) {
      if (card?.state === "opened" || card?.state === "dismissed")
        out.set(id, card.state);
    }
  }
  return out;
};

/** The provider value for a chat. */
export const useSupportCards = (chat: SupportChat): SupportCards => {
  const { messages, setSupportCard } = chat;
  const states = useMemo(() => supportCardStatesIn(messages), [messages]);
  return useMemo(
    () => ({
      stateOf: (id: string) => states.get(id) ?? null,
      setState: setSupportCard
    }),
    [states, setSupportCard]
  );
};
